import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Grid, TextField, Button, Typography, Divider, Stack, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Snackbar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../services/api";
import { getToken } from "../services/auth";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

const EMPTY_SETTINGS = {
  SalesDepositAccount: "", DefaultSalesAccount: "",
  BankAccount: "", CashRegisterAccount: "", DefaultPurchaseAccount: "",
};

/* Editable override table (material -> account) */
function OverrideTable({ rows, setRows, placeholderAcct }) {
  const set = (i, patch) => setRows((p) => p.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow><TableCell>Material (item)</TableCell><TableCell>QuickBooks account</TableCell><TableCell /></TableRow>
        </TableHead>
        <TableBody>
          {rows.map((it, i) => (
            <TableRow key={i}>
              <TableCell><TextField fullWidth size="small" placeholder="e.g. ZORBA" value={it.item}
                onChange={(e) => set(i, { item: e.target.value })} /></TableCell>
              <TableCell><TextField fullWidth size="small" placeholder={placeholderAcct} value={it.account}
                onChange={(e) => set(i, { account: e.target.value })} /></TableCell>
              <TableCell><Tooltip title="Remove"><IconButton size="small" color="error"
                onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}><DeleteIcon fontSize="small" /></IconButton></Tooltip></TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow><TableCell colSpan={3} sx={{ color: "text.secondary" }}>No overrides — uses the default account.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <Button size="small" sx={{ mt: 0.5 }} onClick={() => setRows((p) => [...p, { item: "", account: "" }])}>+ Add material</Button>
    </Box>
  );
}

export default function QuickBooks() {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [items, setItems] = useState([]);            // sales overrides
  const [itemsPurchase, setItemsPurchase] = useState([]); // purchase overrides
  const [msg, setMsg] = useState(null);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());

  const load = useCallback(() => {
    api.get("/quickbooks/mapping").then((r) => {
      setSettings({ ...EMPTY_SETTINGS, ...(r.data?.settings || {}) });
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setItemsPurchase(Array.isArray(r.data?.itemsPurchase) ? r.data.itemsPurchase : []);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveMapping = () => {
    api.put("/quickbooks/mapping", { settings, items, itemsPurchase })
      .then(() => setMsg("Mapping saved"))
      .catch((e) => setMsg(e.response?.data?.error || "Save failed"));
  };

  const download = (endpoint, needed) => {
    if (!from || !to) { setMsg("Pick a date range"); return; }
    for (const [field, label] of needed) {
      if (!settings[field]) { setMsg(`Set "${label}" in Account Mapping first`); return; }
    }
    const base = api.defaults.baseURL || "";
    const token = getToken();
    window.location.href = `${base}/quickbooks/export/${endpoint}?fromDate=${from}&toDate=${to}${token ? `&token=${encodeURIComponent(token)}` : ""}`;
  };

  const field = (k, label, helper) => (
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label={label} helperText={helper} value={settings[k]} onChange={(e) => setS(k, e.target.value)} />
    </Grid>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>QuickBooks</Typography>

      {/* ---------- Account mapping ---------- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Account Mapping</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Type your QuickBooks account names exactly as they appear in QuickBooks.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Sales</Typography>
        <Grid container spacing={2}>
          {field("SalesDepositAccount", "Sales deposit account (money in)", "the bank/deposit account your sales go into")}
          {field("DefaultSalesAccount", "Default Sales account", "for any material without an override")}
        </Grid>
        <Box sx={{ mt: 1 }}><OverrideTable rows={items} setRows={setItems} placeholderAcct="e.g. Zorba Sales" /></Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Bank &amp; Cash Register</Typography>
        <Grid container spacing={2}>
          {field("BankAccount", "Bank account (Glomets)", "money pulled from here into the register")}
          {field("CashRegisterAccount", "Cash In Register account", "your DailyCash register")}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Purchases</Typography>
        <Grid container spacing={2}>
          {field("DefaultPurchaseAccount", "Default Purchase account", "for any material without an override")}
        </Grid>
        <Box sx={{ mt: 1 }}><OverrideTable rows={itemsPurchase} setRows={setItemsPurchase} placeholderAcct="e.g. Copper Purchases" /></Box>

        <Box sx={{ mt: 2 }}><Button variant="contained" onClick={saveMapping}>Save mapping</Button></Box>
      </Paper>

      {/* ---------- Exports ---------- */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Exports</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Each export is a QuickBooks <b>.IIF</b> file. In QuickBooks Desktop: <b>File → Utilities → Import → IIF Files</b>.
          Test on a <b>test company</b> first.
        </Alert>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>

        <Stack spacing={1.5}>
          <ExportRow title="Sale Invoices" desc="Deposits into your sales account(s)."
            onClick={() => download("sales.iif", [["SalesDepositAccount", "Sales deposit account"], ["DefaultSalesAccount", "Default Sales account"]])} />
          <ExportRow title="Cash Received (from bank)" desc="Transfers: Bank → Cash In Register."
            onClick={() => download("cashReceived.iif", [["BankAccount", "Bank account"], ["CashRegisterAccount", "Cash In Register account"]])} />
          <ExportRow title="Purchase Invoices" desc="Money out of Cash In Register → Purchase account(s)."
            onClick={() => download("purchases.iif", [["CashRegisterAccount", "Cash In Register account"], ["DefaultPurchaseAccount", "Default Purchase account"]])} />
        </Stack>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
          Expenses export coming next.
        </Typography>
      </Paper>

      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)} message={msg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}

function ExportRow({ title, desc, onClick }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", border: "1px solid #e3e8ef", borderRadius: 1, p: 1.5 }}>
      <Box>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{desc}</Typography>
      </Box>
      <Button variant="contained" onClick={onClick}>Download IIF</Button>
    </Box>
  );
}
